'use client';

import React from 'react';
import Link from 'next/link';
import { twMerge } from 'tailwind-merge';
import { useTranslations } from 'next-intl';
import { useLocaleLink } from '@/ui/hooks/useLocaleLink';
import { ButtonVariants } from './types/ButtonVariants';
import Spinner from '../Spinner/Spinner';

export type ButtonProps = {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  variant?: ButtonVariants;
  onClick?: (event?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  type?: 'button' | 'submit' | 'reset';
  href?: string;
  openInNewTab?: boolean | null;
  disabled?: boolean;
  scroll?: boolean;
  loading?: boolean;
  id?: string;
  'aria-label'?: string;
  tabIndex?: number;
  loadingText?: string;
  'data-tile-element-id'?: string;
};

export default function Button({
  children,
  className = '',
  style,
  variant = 'primary',
  onClick,
  type = 'button',
  href,
  openInNewTab,
  disabled = false,
  scroll = true,
  loading,
  id = '',
  'aria-label': ariaLabel,
  tabIndex,
  loadingText,
  'data-tile-element-id': tileElementTrackingId,
}: Readonly<ButtonProps>) {
  const { localizedLink } = useLocaleLink();
  const t = useTranslations('Button');
  const resolvedLoadingText = loadingText ?? t('loading');

  const focusRingClasses =
    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#406AFF]';

  const variantClasses = {
    primary: [
      'bg-primary-100 border-0 rounded-full',
      'shadow-[inset_0_0_0_1px_#000]',
      'hover:bg-white',
      'active:bg-white active:shadow-[inset_0_0_0_1px_#000,inset_0_4px_4px_rgba(0,0,0,0.25)]',
      focusRingClasses,
    ].join(' '),
    secondary: [
      'bg-white border-0 rounded-full',
      'shadow-[inset_0_0_0_1px_#000]',
      'hover:bg-neutral-100 hover:text-white hover:shadow-[inset_0_0_0_2px_#000]',
      'active:bg-neutral-100 active:text-white active:shadow-[inset_0_0_0_2px_#000,inset_0_4px_4px_rgba(255,255,255,0.25)]',
      focusRingClasses,
    ].join(' '),
    secondaryBlack: [
      'bg-neutral-100 border-0 text-white rounded-full',
      'shadow-[inset_0_0_0_1px_#000]',
      'hover:bg-white hover:text-neutral-100 hover:shadow-[inset_0_0_0_2px_#000]',
      'active:bg-white active:text-neutral-100 active:shadow-[inset_0_0_0_2px_#000,inset_0_4px_4px_rgba(0,0,0,0.25)]',
      focusRingClasses,
    ].join(' '),
    tertiary: `bg-neutral-100 text-white border-neutral-100 focus:border-white hover:border-white focus:shadow-[0_3px_5px_0px_rgba(255,255,255,0.35)inset] rounded-full ${focusRingClasses}`,
    quaternary: `border-red text-red border font-medium py-2 focus:shadow-[0_3px_5px_0px_rgba(255,255,255,0.35)inset] rounded-full ${focusRingClasses}`,
    quinary: [
      'bg-neutral-10 border border-neutral-100 rounded-full',
      'hover:bg-white',
      'active:bg-white active:shadow-[inset_0_4px_4px_rgba(0,0,0,0.25)]',
      focusRingClasses,
    ].join(' '),
    black: `bg-neutral-100 border-neutral-100 text-white hover:bg-neutral-80 rounded-full ${focusRingClasses}`,
    whiteBlackBorder: `inline-flex items-center justify-center rounded-lg border border-neutral-100 font-semibold uppercase ${focusRingClasses}`,
    yellowBlackBorder: `inline-flex items-center justify-center rounded-lg border border-neutral-100 font-semibold uppercase bg-primary-100 hover:bg-white ${focusRingClasses}`,
    destructive: [
      'bg-white border-0 rounded-full text-[#EC041B]',
      'shadow-[inset_0_0_0_1px_#EC041B]',
      'hover:text-neutral-100 hover:shadow-[inset_0_0_0_1px_#EC041B]',
      'active:text-neutral-100 active:shadow-[inset_0_0_0_1px_#EC041B,inset_0_4px_4px_rgba(0,0,0,0.25)]',
      focusRingClasses,
    ].join(' '),
  };

  const joinedClasses = twMerge(
    'font-bold relative uppercase py-3 px-5 text-center border-2 text-sm transition-[background-color,box-shadow,color,border-color] duration-200 ease-in text-neutral-100 block',
    disabled
      ? 'cursor-not-allowed px-5 text-neutral-50 border border-neutral-40 bg-neutral-10 rounded-full'
      : variantClasses[variant],
    disabled && variant === 'yellowBlackBorder' ? 'bg-white rounded-lg' : '',
    className,
    loading && 'text-opacity-0 pointer-events-none',
  );

  const darkBgVariants: ButtonVariants[] = ['secondaryBlack', 'tertiary', 'black'];
  const spinnerColorClass = darkBgVariants.includes(variant) ? 'text-white' : 'text-neutral-100';

  if (href) {
    return (
      <Link
        href={
          href.startsWith('mailto:') ||
          href.startsWith('tel:') ||
          href.startsWith('#') ||
          href.startsWith('?')
            ? href
            : localizedLink(href)
        }
        target={openInNewTab ? '_blank' : undefined}
        className={joinedClasses}
        style={style}
        onClick={onClick}
        aria-disabled={disabled || loading}
        aria-busy={loading || undefined}
        aria-label={ariaLabel}
        tabIndex={disabled ? -1 : (tabIndex ?? undefined)}
        scroll={scroll}
        id={id}
        data-tile-element-id={tileElementTrackingId}
      >
        {children}
        {loading && (
          <>
            <span className="sr-only">{resolvedLoadingText}</span>
            <div
              className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform ${spinnerColorClass} text-opacity-100`}
            >
              <Spinner />
            </div>
          </>
        )}
      </Link>
    );
  }

  return (
    // eslint-disable-next-line react/button-has-type
    <button
      type={type}
      className={joinedClasses}
      style={style}
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      aria-label={ariaLabel}
      tabIndex={disabled ? -1 : (tabIndex ?? undefined)}
      id={id}
      data-tile-element-id={tileElementTrackingId}
    >
      {children}
      {loading && (
        <>
          <span className="sr-only">{resolvedLoadingText}</span>
          <div
            className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform ${spinnerColorClass} text-opacity-100`}
          >
            <Spinner />
          </div>
        </>
      )}
    </button>
  );
}
